import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

/**
 * Death-verification settings (s87 F86-5): how executor access unlocks after the
 * planner's passing is reported. PLAN-LEVEL (not per-executor) — extracted from the
 * retired ExecutorManagement and rehomed under Settings → My Executor when the executor
 * section became a plain expandable list. (Was inline under Legal; see decisions-log.)
 */
const VERIFICATION_METHOD_LABELS: Record<string, string> = {
  advisor_confirms: "Advisor confirms",
  death_certificate: "Death certificate",
  grace_period: "Grace period",
  immediate_trust: "Immediate trust",
};

const VERIFICATION_METHOD_DESCRIPTIONS: Record<string, string> = {
  immediate_trust:
    "Your executor gets access as soon as they report your passing. Simplest and fastest — best when you fully trust your executor.",
  death_certificate:
    "Your executor provides a death certificate when reporting your passing — an added layer of proof.",
  grace_period:
    "Access is held for a waiting period after your passing is reported — a window to catch and cancel a report made in error.",
  advisor_confirms:
    "Your financial advisor confirms your passing before access opens. Best when an advisor is closely involved with your estate.",
};

export function DeathVerificationSettings({ clientId }: { clientId: string }) {
  const { toast } = useToast();
  const [deathMethod, setDeathMethod] = useState("advisor_confirms");
  const [gracePeriodHours, setGracePeriodHours] = useState(168);

  const { data: deathSettings } = useQuery<{ deathVerificationMethod: string; gracePeriodHours: number }>({
    queryKey: ["/api/clients", clientId, "death-verification-settings"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/death-verification-settings`);
      if (!res.ok) throw new Error("Failed to load settings");
      return res.json();
    },
    enabled: !!clientId,
  });

  useEffect(() => {
    if (deathSettings) {
      setDeathMethod(deathSettings.deathVerificationMethod);
      setGracePeriodHours(deathSettings.gracePeriodHours ?? 168);
    }
  }, [deathSettings]);

  const mutation = useMutation({
    mutationFn: async (settings: { deathVerificationMethod: string; gracePeriodHours: number }) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientId}/death-verification-settings`, settings);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "death-verification-settings"] });
      toast({ title: "Saved", description: "Death verification settings updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save settings.", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Death Verification Settings</CardTitle>
        <CardDescription>
          How should executor access be unlocked after your passing is reported?
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Verification Method</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Verification Method">
            {Object.entries(VERIFICATION_METHOD_LABELS).map(([value, label]) => {
              const selected = deathMethod === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setDeathMethod(value);
                    mutation.mutate({ deathVerificationMethod: value, gracePeriodHours });
                  }}
                  className={`text-left rounded-lg border p-3 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#5C7F3F] ${
                    selected ? "border-[#5C7F3F] bg-[#5C7F3F]/5 ring-1 ring-[#5C7F3F]" : "border-border hover:bg-muted/50"
                  }`}
                  data-testid={`radio-death-method-${value}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${
                        selected ? "border-[#5C7F3F] bg-[#5C7F3F]" : "border-muted-foreground/40"
                      }`}
                    />
                    <span className="font-medium text-sm">{label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {VERIFICATION_METHOD_DESCRIPTIONS[value]}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {deathMethod === "grace_period" && (
          <div className="space-y-2">
            <Label htmlFor="grace-period">Wait Period</Label>
            <Select
              value={String(gracePeriodHours)}
              onValueChange={(v) => {
                const hours = Number(v);
                setGracePeriodHours(hours);
                mutation.mutate({ deathVerificationMethod: deathMethod, gracePeriodHours: hours });
              }}
            >
              <SelectTrigger id="grace-period" data-testid="select-grace-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="48">2 days</SelectItem>
                <SelectItem value="168">7 days (default)</SelectItem>
                <SelectItem value="336">14 days</SelectItem>
                <SelectItem value="720">30 days</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              A report can't be stopped from a screen yet. A one-click stop is coming. Full access unlocks automatically when the timer expires.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
